<script setup lang="ts">
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import type { UserHostInvitee } from "@/models"
import type { ToastHtmlElement } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { useMediaQuery } from "@vueuse/core"
import { computed, onMounted, reactive, ref, useTemplateRef } from "vue"
import { LIST_CO_HOSTS_KEY, useDeleteCoHost, useInviteCoHost, useListHostInvitees, useTransferEventOwnership } from "@/api"
import { CoHostListActionsMenu as ActionsMenu, AddCoHostDrawer, ConfirmModal, DataCard } from "@/components"
import { Button, ButtonGroup, Icon, Table, Toast } from "@/components/ui"
import { useConvertedRouteParam, useModal, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { HostEventAccessType, HostInviteeStatus } from "@/models"
import { AddCoHostDrawerKey, ConfirmModalKey } from "@/symbols"
import { ViewMode } from "@/utils"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t } = useTranslation()

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/users", value => Number(value))

const successToast = useTemplateRef<ToastHtmlElement | null>("successToast")
const errorToast = useTemplateRef<ToastHtmlElement | null>("errorToast")

const { toggle: toggleAddCoHostDrawer } = useModal(AddCoHostDrawerKey)
const { toggle: toggleConfirmModal, isActive: isConfirmModalOpen } = useModal(ConfirmModalKey)

const mode = ref<ViewMode>(ViewMode.TABLE)

const isMobileView = useMediaQuery("(max-width: 1024px)")

enum ActionType {
  Transfer = "transfer",
  Reinvite = "reinvite",
  Remove = "remove",
}

const selectedHostInvitee = ref<UserHostInvitee>()
const actionType = ref<ActionType>()
const confirmModalConfig = reactive({ title: "", message: "" })
const client = useQueryClient()

const { data: usersData, isLoading: isLoadingUsers } = useListHostInvitees(eventVaultId)

const isLoading = computed(() => isLoadingUsers.value)

interface UsersTableRow {
  name?: string
  email?: string
  status?: HostInviteeStatus
  accessLevel?: HostEventAccessType
  canTransferOwnership?: boolean
  canBeRemoved?: boolean
  canBeReinvited?: boolean
  actions?: unknown
}

const usersTableData = computed<UsersTableRow[]>(() => {
  return usersData?.value?.users?.map(user => ({
    name: user.name,
    email: user.email,
    status: user.status,
    accessLevel: user.accessLevel,
    canTransferOwnership: user.canTransferOwnership,
    canBeRemoved: user.canBeRemoved,
    canBeReinvited: user.canBeReinvited,
  })) ?? []
})

const headers = computed(() => {
  const baseHeaders: ITableHeader<UsersTableRow>[] = [
    { label: t("event_co_host_list_page.headers.name"), value: "name" },
    { label: t("event_co_host_list_page.headers.email"), value: "email" },
    { label: t("event_co_host_list_page.headers.status"), value: "status" },
  ]

  if (usersData.value?.allowUpdatingUsers) {
    baseHeaders.push({ label: t("event_co_host_list_page.headers.actions"), value: "actions" })
  }

  return baseHeaders
})

onMounted(() => {
  if (isMobileView.value) {
    mode.value = ViewMode.CARD
  }
})

function setConfirmModalConfig({ title, message }: { title: string, message: string }) {
  confirmModalConfig.title = title
  confirmModalConfig.message = message
}

function onTransferOwnershipTo(hostInvitee?: UserHostInvitee) {
  selectedHostInvitee.value = hostInvitee
  setConfirmModalConfig({
    title: t("event_co_host_list_page.confirm_transfer.title", { name: selectedHostInvitee.value?.name }),
    message: t("event_co_host_list_page.confirm_transfer.message", { name: selectedHostInvitee.value?.name }),
  })
  actionType.value = ActionType.Transfer
  toggleConfirmModal()
}

function onReinvite(hostInvitee?: UserHostInvitee) {
  selectedHostInvitee.value = hostInvitee
  setConfirmModalConfig({
    title: t("event_co_host_list_page.confirm_reinvite.title", { name: selectedHostInvitee.value?.name }),
    message: t("event_co_host_list_page.confirm_reinvite.message", { name: selectedHostInvitee.value?.name }),
  })
  actionType.value = ActionType.Reinvite
  toggleConfirmModal()
}

function onRemove(hostInvitee?: UserHostInvitee) {
  selectedHostInvitee.value = hostInvitee
  setConfirmModalConfig({
    title: t("event_co_host_list_page.confirm_remove.title", { name: selectedHostInvitee.value?.name }),
    message: t("event_co_host_list_page.confirm_remove.message", { name: selectedHostInvitee.value?.name }),
  })
  actionType.value = ActionType.Remove
  toggleConfirmModal()
}

const { mutate: mutateReinviteUser, isPending: isReinvitingUser } = useInviteCoHost(eventVaultId.value)
const { mutate: mutateRemoveCoHost, isPending: isRemovingCoHost } = useDeleteCoHost(eventVaultId.value)
const { mutate: mutateTransferOwnership, isPending: isTransferringOwnership } = useTransferEventOwnership(eventVaultId.value)

async function handleMutationSuccess(message: string) {
  await client.invalidateQueries({ queryKey: [LIST_CO_HOSTS_KEY, eventVaultId] })
  showSuccessToast(message)
  if (isConfirmModalOpen.value)
    toggleConfirmModal()
  clearModalConfig()
}

function mutateConfirmAction() {
  if (!selectedHostInvitee.value || !actionType.value)
    return

  const { email = "", name = "" } = selectedHostInvitee.value

  switch (actionType.value) {
    case ActionType.Transfer:
      mutateTransferOwnership(email, {
        onSuccess: () => handleMutationSuccess(t("event_co_host_list_page.confirm_transfer.success", { name })),
        onError: () => showErrorToast(),
      })
      break
    case ActionType.Reinvite:
      mutateReinviteUser({ email, name }, {
        onSuccess: () => handleMutationSuccess(t("event_co_host_list_page.confirm_reinvite.success", { name })),
        onError: () => showErrorToast(),
      })
      break
    case ActionType.Remove:
      mutateRemoveCoHost(email, {
        onSuccess: () => handleMutationSuccess(t("event_co_host_list_page.confirm_remove.success", { name })),
        onError: () => showErrorToast(),
      })
      break
  }
}

function clearModalConfig() {
  selectedHostInvitee.value = undefined
  actionType.value = undefined
}

function showSuccessToast(message: string) {
  successToast.value?.showToast({ title: "Success", content: message, icon: "CircleCheck" })
}

function showErrorToast() {
  errorToast.value?.showToast()
}
</script>

<template>
  <SidebarHostEventLayout>
    <div class="flex flex-col gap-6">
      <div class="flex justify-between gap-2 items-center">
        <h1 class="font-comfortaa font-bold text-xl">
          {{ t("event_co_host_list_page.title") }}
        </h1>
        <div class="flex gap-2">
          <ButtonGroup>
            <Button
              data-test="table_view_button" :variant="mode === ViewMode.TABLE ? 'tertiary' : 'primary'"
              :disabled="mode === ViewMode.TABLE" @click="mode = ViewMode.TABLE"
            >
              <Icon icon="Table" />
              <span class="hidden md:block">{{ t("host_dashboard.table_view") }}</span>
              <span class="sr-only">{{ t("host_dashboard.select_table_view") }}</span>
            </Button>
            <Button
              data-test="card_view_button" :variant="mode === ViewMode.CARD ? 'tertiary' : 'primary'"
              :disabled="mode === ViewMode.CARD" @click="mode = ViewMode.CARD"
            >
              <Icon icon="Cards" />
              <span class="hidden md:block">{{ t("host_dashboard.card_view") }}</span>
              <span class="sr-only">{{ t("host_dashboard.select_card_view") }}</span>
            </Button>
          </ButtonGroup>
          <Button
            v-if="usersData?.allowAddingUsers"
            prefix-icon="UserPlus"
            data-test="add-user-button"
            @click.prevent="toggleAddCoHostDrawer"
          >
            <span class="sr-only md:not-sr-only whitespace-nowrap">{{ t("event_co_host_list_page.add_user_btn") }}</span>
          </Button>
        </div>
      </div>

      <Transition name="gpx-fade" mode="out-in">
        <Table v-if="mode === ViewMode.TABLE" :headers="headers" :data="usersTableData" :is-loading="isLoading" compact>
          <template #data-name="{ data: row }">
            {{ row.accessLevel === HostEventAccessType.Owner ? `${row.name}
                                (${t("event_co_host_list_page.owner_label")})` : row.name }}
          </template>
          <template #data-status="{ data: row }">
            {{ row.status === HostInviteeStatus.Active ? t('event_co_host_list_page.active_label')
              : t('event_co_host_list_page.invitation_sent_label') }}
          </template>
          <template #data-actions="{ data: row }">
            <ActionsMenu
              :host-invitee="row" @transfer-ownership-to="onTransferOwnershipTo"
              @reinvite="onReinvite" @remove="onRemove"
            />
          </template>
        </Table>
        <div v-else>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <DataCard
              v-for="(user, index) in usersTableData"
              :key="`user-${index}`"
              :data="user"
              :headers="headers"
            >
              <template #data-name="{ data: row }">
                {{
                  row.accessLevel === HostEventAccessType.Owner
                    ? `${row.name} (${t("event_co_host_list_page.owner_label")})`
                    : row.name
                }}
              </template>
              <template #data-status="{ data: row }">
                {{
                  row.status === HostInviteeStatus.Active
                    ? t('event_co_host_list_page.active_label')
                    : t('event_co_host_list_page.invitation_sent_label')
                }}
              </template>
              <template #data-actions="{ data: row }">
                <ActionsMenu
                  :host-invitee="row"
                  @transfer-ownership-to="onTransferOwnershipTo"
                  @reinvite="onReinvite"
                  @remove="onRemove"
                />
              </template>
            </DataCard>
          </div>
        </div>
      </Transition>
    </div>

    <AddCoHostDrawer
      v-if="usersData?.allowAddingUsers" :event-vault-id="eventVaultId" @success="showSuccessToast"
      @error="showErrorToast"
    />

    <ConfirmModal
      v-if="confirmModalConfig" :title="confirmModalConfig.title" :message="confirmModalConfig.message"
      :is-loading="isReinvitingUser || isRemovingCoHost || isTransferringOwnership" @confirm="mutateConfirmAction"
      @cancel="clearModalConfig"
    />

    <Toast ref="successToast" variant="success" />
    <Toast ref="errorToast" variant="danger" />
  </SidebarHostEventLayout>
</template>
